// Mock leaderboard world — port of the design project's mockData.js.
// A single module-level engine instance is shared by the dashboard preview
// card and the full screen so both views show the same simulated board.
// Swapped out for the real API by the USE_MOCK flag in leaderboardService.ts.

import Environment from '../../../environment';
import {mapEngagementWinnersEnvelope} from './leaderboardService';
import {
  EngagementSummary,
  EngagementWinners,
  LeaderboardSnapshot,
  MilestoneVM,
  Period,
  RankingCriteria,
} from './types';

// Deterministic seeded generator so the roster is stable across reloads.
function makeRng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

const NAMES = [
  'KnightRider64',
  'QueensGambit',
  'RookiePro',
  'BishopBlitz',
  'CaissaDreams',
  'ZugzwangZoe',
  'EnPassantEli',
  'TalDisciple',
  'PetrosianWall',
  'FischerHeat',
  'CarlsenJr',
  'BlitzBaron',
  'EndgameOwl',
  'PawnStormer',
  'SicilianDrag',
  'GrandPatzer',
  'TempoTian',
  'KingsIndianK',
  'BerlinDefender',
  'LondonSystem',
  'NajdorfNina',
  'FianchettoFox',
  'OpeningTheo',
  'RapidRaptor',
  'StalemateSam',
  'GambitGoblin',
  'CheckmateCat',
  'SwiftKnight',
];

export const MOCK_YOU_USERID = -1;

interface MockPlayer {
  userid: number;
  name: string;
  rating: number;
  you: boolean;
  wins: Record<'daily' | 'weekly', number>;
  // Current streak length (days) — used by the 'streaks' tab only.
  streak: number;
}

function buildPlayers(): MockPlayer[] {
  const rnd = makeRng(20260708);
  const skew = () => rnd() * rnd(); // bias toward smaller values → realistic long tail
  const players: MockPlayer[] = NAMES.map((name, i) => {
    const rating = 640 + Math.round(rnd() * 1520);
    const dW = 3 + Math.round(skew() * 26);
    const wW = 22 + Math.round(skew() * 120);
    const streak = 1 + Math.round(skew() * 41);
    return {
      userid: 1000 + i,
      name,
      rating,
      you: false,
      wins: {daily: dW, weekly: wW},
      streak,
    };
  });
  players.push({
    userid: MOCK_YOU_USERID,
    name: 'You',
    rating: 862,
    you: true,
    wins: {daily: 3, weekly: 118},
    streak: 4,
  });
  return players;
}

// Metric shown for the active tab: wins for daily/weekly, streak days for
// 'streaks'/'streak_count'.
function metricOf(
  p: MockPlayer,
  period: Period,
  criteria: RankingCriteria,
): number {
  if (criteria === 'streak_count' || period === 'streaks') {
    return p.streak;
  }
  return p.wins[period as 'daily' | 'weekly'];
}

function rankOrder(
  players: MockPlayer[],
  period: Period,
  criteria: RankingCriteria,
): MockPlayer[] {
  return [...players].sort(
    (a, b) =>
      metricOf(b, period, criteria) - metricOf(a, period, criteria) ||
      b.rating - a.rating ||
      a.userid - b.userid,
  );
}

type Listener = () => void;

class MockLeaderboardEngine {
  private players = buildPlayers();
  private listeners = new Set<Listener>();
  private timer: ReturnType<typeof setInterval> | null = null;
  // userids whose wins changed on the latest tick (drives the row flash).
  lastBumped = new Set<number>();

  // Let the caller brand the "you" row with the real signed-in identity.
  setYou(name?: string, rating?: number) {
    const you = this.players.find(p => p.you);
    if (!you) {
      return;
    }
    let changed = false;
    if (name && you.name !== name) {
      you.name = name;
      changed = true;
    }
    if (rating && rating > 0 && you.rating !== rating) {
      you.rating = rating;
      changed = true;
    }
    if (changed) {
      this.emit();
    }
  }

  // Builds a LeaderboardSnapshot directly in our internal shape (metric
  // always keyed `wins`) — the API's criteria-dependent field names
  // (`wins`/`leader_wins` vs `streak_count`/`leader_streak_count`) only exist
  // in leaderboardService.ts's mapEnvelope, which the mock bypasses entirely.
  snapshot(
    period: Period,
    criteria: RankingCriteria = 'game_wins',
  ): LeaderboardSnapshot {
    const ordered = rankOrder(this.players, period, criteria);
    const entries = ordered.map((p, i) => ({
      userid: p.userid,
      rank: i + 1,
      name: p.name,
      rating: p.rating,
      wins: metricOf(p, period, criteria),
      you: p.you,
    }));
    const meRow = entries.find(e => e.you)!;
    return {
      period,
      entries,
      me: {
        rank: meRow.rank,
        wins: meRow.wins,
        rating: meRow.rating,
        name: meRow.name,
        inTop: meRow.rank <= 20,
      },
      totalPlayers: entries.length,
      leaderWins: entries[0]?.wins ?? 0,
    };
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    if (!this.timer) {
      this.timer = setInterval(() => this.tick(), 2300);
    }
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0 && this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    };
  }

  // One simulated batch of finished games (1-2 winners), like the design.
  private tick() {
    const humans = this.players.filter(p => !p.you);
    const n = 1 + Math.floor(Math.random() * 2);
    this.lastBumped = new Set<number>();
    for (let k = 0; k < n; k++) {
      const p = humans[Math.floor(Math.random() * humans.length)];
      p.wins.daily += 1;
      p.wins.weekly += 1;
      this.lastBumped.add(p.userid);
    }
    this.emit();
  }

  private emit() {
    this.listeners.forEach(l => l());
  }
}

export const mockLeaderboardEngine = new MockLeaderboardEngine();

// ---------------------------------------------------------------------------
// Engagement (Streak & Rewards) mock — port of the design canvas's SCENARIOS
// map (scratchpad/engagement_canvas.dc.html). Lets the page render every
// visual state locally while POST /dashboard/v1/engagement-summary/ is dark.
// Toggle via env.local.js: ENGAGEMENT_USE_MOCK + ENGAGEMENT_MOCK_SCENARIO.
// ---------------------------------------------------------------------------

export type EngagementScenarioKey =
  | 'fresh'
  | 'mid'
  | 'oneAway'
  | 'eligible'
  | 'atRisk'
  | 'week2Track'
  | 'week3Track'
  | 'grandTrack'
  | 'allIn';

interface ScenarioFixture {
  streakCount: number;
  todayComplete: boolean;
  // Mock-only override; real data derives "at risk" from a time-of-day
  // heuristic (see engagementPresentation.ts) since the API contract has no
  // explicit at-risk flag.
  forceAtRisk: boolean;
}

const SCENARIOS: Record<EngagementScenarioKey, ScenarioFixture> = {
  fresh: {streakCount: 0, todayComplete: false, forceAtRisk: false},
  mid: {streakCount: 4, todayComplete: true, forceAtRisk: false},
  oneAway: {streakCount: 6, todayComplete: false, forceAtRisk: false},
  eligible: {streakCount: 7, todayComplete: true, forceAtRisk: false},
  atRisk: {streakCount: 5, todayComplete: false, forceAtRisk: true},
  // Post-day-7 segment-mode scenarios (weekly_draw done, walking the day-dot
  // path toward the next checkpoint/grand draw) — see PathToRewardsStrip's
  // segment layout.
  week2Track: {streakCount: 10, todayComplete: true, forceAtRisk: false},
  week3Track: {streakCount: 17, todayComplete: true, forceAtRisk: false},
  grandTrack: {streakCount: 25, todayComplete: true, forceAtRisk: false},
  allIn: {streakCount: 30, todayComplete: true, forceAtRisk: false},
};

function buildMilestones(streakCount: number): MilestoneVM[] {
  return [
    {
      id: 'weekly_draw',
      days: 7,
      period: 'weekly',
      daysLeft: Math.max(0, 7 - streakCount),
      achievedThisPeriod: streakCount >= 7,
      prize: {
        title: 'Wireless Headphones',
        short: 'Weekly Draw',
        description: 'Weekly Draw · every Sunday',
      },
    },
    {
      id: 'week_2',
      days: 14,
      period: 'weekly',
      kind: 'week_checkpoint',
      label: 'Week 2',
      daysLeft: Math.max(0, 14 - streakCount),
      achievedThisPeriod: streakCount >= 14,
      prize: {
        title: 'Week 2',
        short: 'Week 2',
      },
    },
    {
      id: 'week_3',
      days: 21,
      period: 'weekly',
      kind: 'week_checkpoint',
      label: 'Week 3',
      daysLeft: Math.max(0, 21 - streakCount),
      achievedThisPeriod: streakCount >= 21,
      prize: {
        title: 'Week 3',
        short: 'Week 3',
      },
    },
    {
      id: 'monthly_grand',
      days: 30,
      period: 'monthly',
      daysLeft: Math.max(0, 30 - streakCount),
      achievedThisPeriod: streakCount >= 30,
      prize: {
        title: 'Gukesh Signed Chess Board',
        short: 'Gukesh Grand Draw',
        description: 'Grand Draw · 31 Aug',
      },
    },
  ];
}

// The env flag names an override key + optional scenario; unrecognised
// scenario values fall back to 'mid'.
const ENV = Environment as {
  ENGAGEMENT_MOCK_SCENARIO?: string;
};

export const DEFAULT_ENGAGEMENT_SCENARIO: EngagementScenarioKey =
  (ENV?.ENGAGEMENT_MOCK_SCENARIO as EngagementScenarioKey) in SCENARIOS
    ? (ENV.ENGAGEMENT_MOCK_SCENARIO as EngagementScenarioKey)
    : 'mid';

// Exposed for the mock only — forceAtRisk isn't part of the real API shape,
// so it's threaded through EngagementSummary's consumers via
// computeEngagementPresentation's `forceAtRisk` option, keyed off this map.
export const MOCK_FORCE_AT_RISK: Record<EngagementScenarioKey, boolean> =
  Object.fromEntries(
    Object.entries(SCENARIOS).map(([k, v]) => [k, v.forceAtRisk]),
  ) as Record<EngagementScenarioKey, boolean>;

export function mockEngagementSummary(
  scenario: EngagementScenarioKey = DEFAULT_ENGAGEMENT_SCENARIO,
): EngagementSummary {
  const {streakCount, todayComplete} = SCENARIOS[scenario] || SCENARIOS.mid;
  const milestones = buildMilestones(streakCount);
  const nextMilestone =
    milestones.find(m => !m.achievedThisPeriod) ||
    milestones[milestones.length - 1] ||
    null;
  return {
    segment: 'casual',
    streak: {
      streakCount,
      lastStreakDate: new Date().toISOString().slice(0, 10),
      todayComplete,
      criteria: [
        {
          id: 4,
          name: 'Win a Game',
          description: 'Win 1 game today',
          targetCount: 1,
          userCount: todayComplete ? 1 : 0,
        },
      ],
    },
    milestones,
    nextMilestone: nextMilestone
      ? {
          id: nextMilestone.id,
          daysLeft: nextMilestone.daysLeft,
          prizeShort: nextMilestone.prize.short,
          label: nextMilestone.label,
        }
      : null,
    // Weekly Top-3 leaderboard prize (Prizes v2) — data-driven card in
    // PrizeCards; absent would hide the card, but the mock always shows it so
    // the three-kind rewards layout is visible offline.
    leaderboardPrize: {
      title: 'CC T-shirt',
      minMetricValue: 5,
      rankFrom: 1,
      rankTo: 3,
    },
    // Always true in mock mode (real gate is Prizes v2 winners_available) so
    // the Winners section stays visible for local review regardless of
    // scenario — including WINNERS_SCENARIO_KEYS' 'empty', which still needs
    // to reach its own empty state rather than being hidden entirely.
    winnersAvailable: true,
    pageEnabled: true,
    serverDate: new Date().toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Winners (Prizes v2 P3) mock — port of the design canvas's winnerPeriods
// scenarios (mkLbWinner/mkDrawWinner in scratchpad/engagement_canvas.dc.html).
// Toggle via env.local.js: ENGAGEMENT_WINNERS_MOCK_SCENARIO ('filled' |
// 'empty' | 'meWon'); gated by the same ENGAGEMENT_USE_MOCK flag above.
//
// Built as snake_case, API-shaped payloads (period_start_date/
// period_end_date, metric_value: null for draws — exactly what
// get_engagement_winners_periods actually emits) pushed through the SAME
// mapEngagementWinnersEnvelope() the live path uses, rather than
// hand-assembled camelCase VMs — a contract drift (renamed/removed field)
// trips in mock mode too, not just against the real API.
// ---------------------------------------------------------------------------

export type WinnersScenarioKey = 'filled' | 'empty' | 'meWon';

const WINNERS_SCENARIO_KEYS: WinnersScenarioKey[] = [
  'filled',
  'empty',
  'meWon',
];
const WINNERS_ENV = Environment as {ENGAGEMENT_WINNERS_MOCK_SCENARIO?: string};

export const DEFAULT_WINNERS_SCENARIO: WinnersScenarioKey =
  WINNERS_SCENARIO_KEYS.indexOf(
    WINNERS_ENV?.ENGAGEMENT_WINNERS_MOCK_SCENARIO as WinnersScenarioKey,
  ) >= 0
    ? (WINNERS_ENV.ENGAGEMENT_WINNERS_MOCK_SCENARIO as WinnersScenarioKey)
    : 'filled';

function mockLbWinnerApi(idx: number, rank: number, wins: number) {
  return {
    userid: 1000 + idx,
    name: NAMES[idx],
    source: 'leaderboard' as const,
    rank,
    metric_value: wins,
    prize: {title: 'CC T-shirt', short: 'CC T-shirt'},
  };
}

// The backend never records a metric_value for draw winners (fix landed in
// dashboard/prizes.py's run_draw/award_winners) — the mock mirrors that null
// rather than a fabricated "N-day streak", exactly like the live payload.
function mockDrawWinnerApi(idx: number, prizeTitle: string, meWon: boolean) {
  return meWon
    ? {
        userid: MOCK_YOU_USERID,
        name: 'You',
        source: 'draw' as const,
        rank: null,
        metric_value: null,
        prize: {title: prizeTitle, short: prizeTitle},
      }
    : {
        userid: 1000 + idx,
        name: NAMES[idx],
        source: 'draw' as const,
        rank: null,
        metric_value: null,
        prize: {title: prizeTitle, short: prizeTitle},
      };
}

function mockWinnerPeriodApi(
  periodType: 'weekly' | 'monthly',
  periodStartDate: string,
  periodEndDate: string,
  winners: ReturnType<typeof mockLbWinnerApi>[],
) {
  return {
    period_type: periodType,
    period_start_date: periodStartDate,
    period_end_date: periodEndDate,
    winners,
  };
}

export function mockWinners(
  scenario: WinnersScenarioKey = DEFAULT_WINNERS_SCENARIO,
): EngagementWinners {
  const meWon = scenario === 'meWon';
  const periods =
    scenario === 'empty'
      ? []
      : [
          mockWinnerPeriodApi('weekly', '2026-07-20', '2026-07-26', [
            mockLbWinnerApi(1, 1, 42),
            mockLbWinnerApi(4, 2, 38),
            mockLbWinnerApi(7, 3, 31),
            mockDrawWinnerApi(9, 'Chess Board', meWon),
          ]),
          mockWinnerPeriodApi('weekly', '2026-07-13', '2026-07-19', [
            mockLbWinnerApi(3, 1, 45),
            mockLbWinnerApi(0, 2, 40),
            mockLbWinnerApi(11, 3, 33),
            mockDrawWinnerApi(13, 'Chess Board', false),
          ]),
          mockWinnerPeriodApi('monthly', '2026-06-01', '2026-06-30', [
            mockDrawWinnerApi(6, 'Gukesh Signed Chess Board', false),
          ]),
        ];
  return mapEngagementWinnersEnvelope({
    success: true,
    data: {
      periods,
      me: {
        has_won: meWon,
        wins: meWon
          ? [
              {
                period_type: 'weekly',
                period_start_date: '2026-07-20',
                period_end_date: '2026-07-26',
                prize: {title: 'Chess Board', short: 'Chess Board'},
                rank: null,
              },
            ]
          : [],
        userid: MOCK_YOU_USERID,
      },
      server_date: new Date().toISOString(),
    },
  });
}
